import { useTranslation } from "react-i18next";

import type { RatingEntry } from "@/entities/rating";
import { formatMoney } from "@/shared/lib";
import { Avatar, Icon } from "@/shared/ui";

/**
 * Подиум первых трёх мест.
 *
 * Вся сцена красится ОДНИМ свойством: каждое место задаёт свой `color`, а
 * корона, кольцо аватара, бейдж, луч и основание читают `currentColor`. Поэтому
 * цветовых правил здесь три штуки, а не пятнадцать.
 *
 * Мест может быть меньше трёх — на свежем стенде их бывает и ноль. Это
 * нормальное состояние, а не ошибка: пустой подиум просто не рисуется, и
 * таблица под ним начинается с первого места.
 *
 * В макете колонки стоят на абсолютных координатах внутри блока 360×178.
 * Здесь вместо этого сетка: колонки тянутся вместе с экраном, а разница высот
 * — единственное, что осталось от абсолютных координат, — задана отступом
 * центральной колонки.
 */
const PLACES = [
  // Порядок в разметке — визуальный: второе место слева, первое по центру.
  { index: 1, tone: "text-podium-second", size: 51 },
  { index: 0, tone: "text-podium-first", size: 66 },
  { index: 2, tone: "text-podium-third", size: 51 },
] as const;

/** Декоративные звёзды: доля ширины и высоты сцены. */
const STARS = [
  { left: 11, top: 33, tone: "text-podium-star" },
  { left: 28, top: 19, tone: "text-podium-star" },
  { left: 77, top: 26, tone: "text-ai-violet" },
  { left: 92, top: 48, tone: "text-ai-violet" },
  { left: 44, top: 42, tone: "text-podium-star" },
] as const;

export function RatingPodium({ top }: { top: RatingEntry[] }) {
  if (top.length === 0) return null;

  return (
    <section className="relative overflow-hidden px-2 pt-8 pb-4">
      {/* Космический фон: концентрические эллипсы повторяющимся градиентом.
          Выходит за края блока, поэтому у секции overflow-hidden. */}
      <span
        aria-hidden="true"
        className={[
          "pointer-events-none absolute -inset-x-6 -top-7 -bottom-3 opacity-75",
          "bg-[repeating-radial-gradient(ellipse_at_50%_67%,color-mix(in_srgb,var(--color-status-info)_9%,transparent)_0_1px,transparent_1px_21px),radial-gradient(ellipse_at_50%_60%,color-mix(in_srgb,var(--color-status-info)_12%,transparent),transparent_48%)]",
        ].join(" ")}
      />
      {STARS.map((star) => (
        <span
          key={`${String(star.left)}-${String(star.top)}`}
          aria-hidden="true"
          className={`pointer-events-none absolute size-0.5 rounded-full bg-current opacity-30 shadow-[0_0_4px_currentColor] ${star.tone}`}
          style={{ left: `${String(star.left)}%`, top: `${String(star.top)}%` }}
        />
      ))}

      <ol className="relative grid grid-cols-[1fr_1.15fr_1fr] items-end gap-1">
        {PLACES.map((place) => {
          const entry = top[place.index];
          if (!entry) return <li key={place.index} aria-hidden="true" />;
          return (
            <PodiumPlace
              key={place.index}
              entry={entry}
              tone={place.tone}
              size={place.size}
              leading={place.index === 0}
            />
          );
        })}
      </ol>

      {/* Линия горизонта под сценой. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-[9%] bottom-3 h-px bg-[linear-gradient(90deg,transparent,color-mix(in_srgb,var(--color-status-info)_18%,transparent),color-mix(in_srgb,var(--color-status-warning)_20%,transparent),color-mix(in_srgb,var(--color-ai-violet)_15%,transparent),transparent)]"
      />
    </section>
  );
}

function PodiumPlace({
  entry,
  tone,
  size,
  leading,
}: {
  entry: RatingEntry;
  tone: string;
  size: number;
  leading: boolean;
}) {
  const { t } = useTranslation();

  return (
    <li
      className={[
        // `isolate` обязателен: луч лежит на `-z-1`, и без собственного
        // контекста наложения он уезжал бы за космический фон сцены — то есть
        // просто исчезал. Нижний отступ отводит место овалу основания, иначе
        // он ложится поверх пилюли с числом роботов.
        "relative isolate flex flex-col items-center pb-3 text-center",
        // Первое место выше остальных — это и делает подиум подиумом.
        leading ? "-mt-6" : "",
        tone,
      ].join(" ")}
    >
      <Icon
        name="crown"
        size={13}
        className="mb-1 drop-shadow-[0_0_var(--glow-blur-sm)_currentColor]"
      />

      <span
        className="relative rounded-full p-[2.5px] shadow-[0_0_13px_color-mix(in_srgb,currentColor_35%,transparent)]"
        style={{
          width: size,
          height: size,
          // Конический градиент от 210° — блик на кольце смещён влево вверх,
          // как источник света всей сцены.
          backgroundImage:
            "conic-gradient(from 210deg, currentColor, rgb(255 255 255 / 0.25), currentColor)",
        }}
      >
        <span
          aria-hidden="true"
          className="absolute -inset-[5px] rounded-full border border-current opacity-[0.27] shadow-[0_0_12px_currentColor]"
        />
        <Avatar
          src={entry.avatar_url}
          initial={entry.initial}
          size={size - 5}
          className="border border-white/25"
        />
        <span
          className={[
            "absolute -bottom-3 left-1/2 flex h-[22px] -translate-x-1/2 items-center justify-center",
            "min-w-[22px] rounded-xs border border-current px-1.5",
            "bg-[linear-gradient(180deg,var(--palette-panel2),var(--palette-bg))]",
            "text-label font-semibold text-text-bright",
            "shadow-[0_5px_10px_rgb(0_0_0/0.35)]",
          ].join(" ")}
        >
          {entry.rank}
        </span>
      </span>

      {/* Луч света. Лежит под содержимым (`-z-1`), поэтому у колонки свой
          контекст наложения — иначе он ушёл бы под фон всей сцены. */}
      <span
        aria-hidden="true"
        className={[
          "pointer-events-none absolute -z-1 left-1/2 w-[85%] -translate-x-1/2 opacity-[0.34] blur-[2px]",
          "bg-[linear-gradient(180deg,color-mix(in_srgb,currentColor_18%,transparent),transparent)]",
          "[clip-path:polygon(32%_0,68%_0,100%_100%,0_100%)]",
        ].join(" ")}
        style={{ top: size + 8, height: 74 }}
      />

      <span className="mt-4 max-w-full truncate text-body font-semibold text-text-bright">
        {entry.display_name}
      </span>
      <span className="mt-1 text-small font-semibold text-status-profit">
        {formatMoney(entry.earned, { sign: "always", suffix: "USDT" })}
      </span>
      <span className="mt-1.5 rounded-sm bg-white/5 px-2 py-0.5 text-caption text-text-secondary">
        {t("rating.robotsCount", { count: entry.robots_count })}
      </span>

      {/* Овал основания. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-1/2 h-2 w-[90%] -translate-x-1/2 rounded-[50%] border border-current opacity-[0.28] shadow-[0_0_9px_currentColor,inset_0_0_8px_color-mix(in_srgb,currentColor_25%,transparent)]"
      />
    </li>
  );
}
